import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import { PointPolygon } from '../../../components/MapView/PointPolygon';
import { useAppSelector } from '../../../store/hooks';
import { useClientData } from '../../useClientData';
import { useGetClientSettingMapView } from '../useGetClientSettingMapView';

export const useLandUseAnalytics = () => {
  const { polygonLandUseAnalytics, pointDataLandUse, isShowPointDataLandUse, isDisplayBoundaryLine, isLayer3D } =
    useAppSelector(mapViewSelector);

  const { getTopSurfaceOpacity, colorTransparent, isSameColorTopSurface, opacity2D, getOpacityExtrusion } =
    useGetClientSettingMapView();

  const { contourSetting } = useClientData();

  const polygonViewer = useCallback(
    (_item: any) => {
      return (
        <Source id={`${_item._id}_landUse`} key={`${_item._id}_landUse`} type="geojson" data={_item.geometry}>
          <Layer
            id={`${_item._id}-polygon`}
            type="line"
            paint={{
              'line-color': _item.color,
              'line-opacity': isDisplayBoundaryLine ? 1 : 0,
              'line-width': 2,
            }}
            source={`${_item._id}_landUse`}
            layout={{ visibility: _item.visible ? 'visible' : 'none' }}
          />
          <Layer
            id={`${_item._id}_landUse_fill-polygon`}
            type="fill"
            paint={{
              'fill-color': _item.color,
              'fill-opacity': !isLayer3D ? opacity2D : 0,
            }}
            source={`${_item._id}_landUse`}
            layout={{ visibility: _item.visible && !isDisplayBoundaryLine ? 'visible' : 'none' }}
          />
          <Layer
            id={`${_item._id}_landUse_top-surface`}
            type="fill-extrusion"
            beforeId={`${_item._id}_landUse_fill-polygon`}
            source={`${_item._id}_landUse`}
            paint={{
              'fill-extrusion-color': _item.color,
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': contourSetting,
              'fill-extrusion-opacity': getTopSurfaceOpacity,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: _item.visible && !isDisplayBoundaryLine ? 'visible' : 'none' }}
          />
          <Layer
            id={`${_item._id}_landUse_side-surface`}
            type="fill-extrusion"
            beforeId={`${_item._id}_landUse_top-surface`}
            source={`${_item._id}_landUse`}
            paint={{
              'fill-extrusion-color': isSameColorTopSurface ? _item.color : colorTransparent,
              'fill-extrusion-height': contourSetting,
              'fill-extrusion-base': 0,
              'fill-extrusion-opacity': getOpacityExtrusion,
              'fill-extrusion-vertical-gradient': false,
            }}
            layout={{ visibility: _item.visible && !isDisplayBoundaryLine ? 'visible' : 'none' }}
          />
        </Source>
      );
    },
    [
      colorTransparent,
      contourSetting,
      getOpacityExtrusion,
      getTopSurfaceOpacity,
      isDisplayBoundaryLine,
      isLayer3D,
      opacity2D,
      isSameColorTopSurface,
    ]
  );

  const layerLandUse = useMemo(() => {
    if (isEmpty(polygonLandUseAnalytics)) {
      return null;
    }

    return (
      <>
        {polygonLandUseAnalytics.map((_item: any) => polygonViewer(_item))}
        {pointDataLandUse &&
          isShowPointDataLandUse &&
          pointDataLandUse.map((_item: any) => <PointPolygon item={_item} />)}
      </>
    );
  }, [polygonLandUseAnalytics, polygonViewer, pointDataLandUse, isShowPointDataLandUse]);

  return { layerLandUse };
};
